/**
 * Pure Session State Machine
 *
 * A reducer function: (state, event) → state
 * No side effects — all side effects are handled by the session manager.
 */

import type { SessionState, SessionEvent } from "./types";

export const INITIAL_STATE: SessionState = {
  status: "idle",
  walletAddress: null,
  walletType: null,
  walletId: null,
  authSubState: null,
  challengeMessage: null,
  blockhash: null,
  authError: null,
  reconnectError: null,
  previousStatus: null,
};

export function sessionReducer(
  state: SessionState,
  event: SessionEvent,
): SessionState {
  switch (state.status) {
    case "idle":
      if (event.type === "RESTORE_START") {
        return { ...state, status: "restoring" };
      }
      if (event.type === "CONNECT") {
        return {
          ...state,
          status: "authenticating",
          walletAddress: event.walletAddress,
          walletType: event.walletType,
          walletId: event.walletId,
          authSubState: "challenging",
          authError: null,
        };
      }
      return state;

    case "restoring":
      if (event.type === "RESTORE_NO_SESSION") return INITIAL_STATE;
      if (event.type === "RESTORE_EXPIRED") return INITIAL_STATE;
      if (event.type === "RESTORE_HOT_WALLET") {
        return {
          ...INITIAL_STATE,
          status: "active",
          walletAddress: event.walletAddress,
          walletType: "hot",
          walletId: event.walletId,
        };
      }
      if (event.type === "RESTORE_LEDGER") {
        return {
          ...INITIAL_STATE,
          status: "wallet_disconnected",
          walletAddress: event.walletAddress,
          walletType: "ledger",
          walletId: event.walletId,
        };
      }
      return state;

    case "authenticating":
      if (event.type === "CHALLENGE_RECEIVED") {
        return {
          ...state,
          authSubState: "previewing",
          challengeMessage: event.message,
          blockhash: event.blockhash ?? null,
          authError: null,
        };
      }
      if (event.type === "CHALLENGE_FAILED") {
        return {
          ...state,
          authSubState: "error",
          authError: event.error,
        };
      }
      if (event.type === "USER_CONFIRM_SIGN") {
        return { ...state, authSubState: "signing", authError: null };
      }
      if (event.type === "SIGNATURE_OBTAINED") {
        return { ...state, authSubState: "verifying" };
      }
      if (event.type === "AUTH_SUCCESS") {
        return {
          ...INITIAL_STATE,
          status: "active",
          walletAddress: state.walletAddress,
          walletType: state.walletType,
          walletId: state.walletId,
        };
      }
      if (event.type === "AUTH_ERROR") {
        return {
          ...state,
          authSubState: "error",
          authError: event.error,
        };
      }
      if (event.type === "AUTH_RETRY") {
        return { ...state, authSubState: "challenging", authError: null };
      }
      if (event.type === "AUTH_CANCEL") {
        // If switching wallets, go back to active with old wallet
        if (state.previousStatus === "active" && event.previousWallet) {
          return {
            ...INITIAL_STATE,
            status: "active",
            walletAddress: event.previousWallet.address,
            walletType: event.previousWallet.type,
            walletId: event.previousWallet.id,
          };
        }
        return INITIAL_STATE;
      }
      if (event.type === "SWITCH_FAILED") {
        return {
          ...INITIAL_STATE,
          status: "active",
          walletAddress: event.previousWallet.address,
          walletType: event.previousWallet.type,
          walletId: event.previousWallet.id,
        };
      }
      return state;

    case "active":
      if (
        event.type === "JWT_EXPIRED" ||
        event.type === "LOGOUT" ||
        event.type === "DISCONNECT"
      ) {
        return INITIAL_STATE;
      }
      if (event.type === "SWITCH_WALLET") {
        return {
          ...INITIAL_STATE,
          status: "authenticating",
          walletAddress: event.walletAddress,
          walletType: event.walletType,
          walletId: event.walletId,
          authSubState: "challenging",
          previousStatus: "active",
        };
      }
      return state;

    case "wallet_disconnected":
      if (event.type === "RECONNECT_SUCCESS") {
        return { ...state, status: "active", reconnectError: null };
      }
      if (event.type === "RECONNECT_REJECTED") {
        return state;
      }
      if (event.type === "RECONNECT_DEVICE_ERROR") {
        return { ...state, reconnectError: event.error };
      }
      if (event.type === "JWT_EXPIRED" || event.type === "LOGOUT") {
        return INITIAL_STATE;
      }
      return state;

    default:
      return state;
  }
}
